import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from './ui/badge';
import { cn } from '@/lib';
import { PriorityStatus } from '@/pages/AdvisorDashboardOpportunities';
import { Skeleton } from '@/components/ui/skeleton';

type OpportunityCardProps = {
  priorityStatus: PriorityStatus;
  cardContent: React.ReactNode;
  cardFooterContent: React.ReactNode;
  isLoading?: boolean;
  ['data-testid']?: string;
};

type OpportunityCardContentProps = {
  icon: React.ReactNode;
  primaryMetric: string;
  metricDescription: string;
  secondaryMetric?: string;
  secondaryMetricDescription?: string;
};

type GenericOpportunityCardContentProps = {
  icon: React.ReactNode;
  subtitle: string;
  title: string;
};

export function GenericOpportunityCardContent({
  icon,
  title,
  subtitle,
}: GenericOpportunityCardContentProps) {
  return (
    <CardContent className="flex flex-col items-center justify-center gap-4">
      <div className="flex flex-col items-center justify-center gap-2">
        <p className="bg-primary-100 mx-auto rounded-full p-3 text-gray-500 ">{icon}</p>
        <div className="mt-2 flex flex-col items-center justify-center gap-[10px]">
          <h2 className="text-primary-dark text-center text-xl font-medium">{title}</h2>
          <p className="text-primary-dark px-12 text-center text-base leading-7">{subtitle}</p>
        </div>
      </div>
    </CardContent>
  );
}

export function OpportunityCardContent({
  icon,
  primaryMetric,
  metricDescription,
  secondaryMetric,
  secondaryMetricDescription,
}: OpportunityCardContentProps) {
  return (
    <CardContent className="flex flex-col items-center justify-center gap-4">
      <div className="flex flex-col items-center justify-center gap-2">
        <p className="bg-primary-100 mx-auto rounded-full p-3 text-gray-500 ">{icon}</p>
        <div className="flex flex-col items-center justify-center gap-1">
          <h2
            data-testid="opportunity-card-primary-metric"
            className="text-primary-dark text-4xl font-semibold"
          >
            {primaryMetric}
          </h2>
          <p
            data-testid="opportunity-card-metric-description"
            className="text-primary-dark px-12 text-center text-base"
          >
            {metricDescription}
          </p>
        </div>
      </div>
      <p className="flex items-center gap-1 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">{secondaryMetric}</span>{' '}
        {secondaryMetricDescription}
      </p>
    </CardContent>
  );
}

export function OpportunityCard({
  priorityStatus,
  cardContent,
  cardFooterContent,
  isLoading = false,
  'data-testid': dataTestId,
}: OpportunityCardProps) {
  if (isLoading) {
    return <OpportunityCardSkeleton />;
  }
  return (
    <Card
      className={cn(
        'border-primary-200 h-auto max-w-[430px] md:w-[363px] md:min-w-[363px] md:max-w-none',
        priorityStatus === 'Not applicable' && 'opacity-50',
      )}
      data-testid={dataTestId}
    >
      <CardHeader>
        <Badge
          className={cn(
            'mx-auto rounded-lg font-normal text-gray-700',
            priorityStatus === 'Highly recommended' && 'bg-red-100 hover:bg-red-200',
            priorityStatus === 'Recommended' && 'bg-yellow-100 hover:bg-yellow-200',
            priorityStatus === 'No action required' && 'bg-green-100 hover:bg-green-200',
            priorityStatus === 'Not applicable' && 'bg-gray-100 hover:bg-gray-200',
          )}
        >
          {priorityStatus}
        </Badge>
      </CardHeader>
      <div className="px-4 pb-4">{cardContent}</div>
      <CardFooter className="flex justify-center gap-2">{cardFooterContent}</CardFooter>
    </Card>
  );
}

export function getClientsEligibleForReminder(
  clients: Client[],
  engagementType: ClientEmailEngagement,
  daysWithoutReminder: number,
): Client[] {
  const xDaysAgo = new Date();
  xDaysAgo.setDate(xDaysAgo.getDate() - daysWithoutReminder);

  return clients.filter(client => {
    const lastReminder = client.latestEmailReminders?.[engagementType as ClientEmailEngagement];
    return !lastReminder || new Date(lastReminder) < xDaysAgo;
  });
}

function OpportunityCardSkeleton() {
  return (
    <Card
      className="border-primary-200 md:h-[346px] md:w-[363px]"
      data-testid="opportunity-card-skeleton-loading"
    >
      <CardHeader>
        <Skeleton className="mx-auto h-5 w-20 rounded-lg" />
      </CardHeader>
      <CardContent className="flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col items-center justify-center gap-3">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="flex flex-col items-center justify-center gap-1">
            <Skeleton className="w-18 mb-2 h-12" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <Skeleton className="h-4 w-32" />
      </CardContent>
      <CardFooter className=" flex justify-center gap-2">
        <Skeleton className="h-10 w-32 rounded-full" />
      </CardFooter>
    </Card>
  );
}
